import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Alert } from '@thegpvc/ui';
import { Trash2, Plus } from 'lucide-react';
import { api } from '../api/client';
import { SectionCard } from './SectionCard';
import { text, bg, border, colWidth, cx } from '../styles/theme';
import { errorMessage } from '../utils/errorMessage';
import type { UserModelPricing } from '../types/api';

// The five per-Mtok rate fields, in display order. Keyed off UserModelPricing so
// the input map stays in sync with the type (and the proto message behind it).
type RateField = Exclude<keyof UserModelPricing, 'modelPrefix'>;
const RATE_FIELDS: { key: RateField; label: string }[] = [
  { key: 'inputPerMtok', label: 'Input' },
  { key: 'outputPerMtok', label: 'Output' },
  { key: 'cacheWritePerMtok', label: 'Cache write' },
  { key: 'cacheReadPerMtok', label: 'Cache read' },
  { key: 'cacheWrite5mPerMtok', label: 'Cache write (5m)' },
];

const BLANK: UserModelPricing = {
  modelPrefix: '',
  inputPerMtok: 0,
  outputPerMtok: 0,
  cacheWritePerMtok: 0,
  cacheReadPerMtok: 0,
  cacheWrite5mPerMtok: 0,
};

// validate returns a client-side error message for an invalid rate, or null.
// The backend validates the same rules, but catching them here avoids a round
// trip and gives the empty-prefix / negative-rate cases an immediate alert.
function validate(rate: UserModelPricing): string | null {
  if (!rate.modelPrefix.trim()) return 'Model prefix is required.';
  for (const f of RATE_FIELDS) {
    const v = rate[f.key];
    if (Number.isNaN(v) || v < 0) return `${f.label} rate must be a number ≥ 0.`;
  }
  return null;
}

const inputClass = (w: string) =>
  cx(w, 'rounded-lg px-3 py-1.5 text-sm border', bg.surface, border.default, text.primary);

/**
 * ModelPricingEditor — the "Custom Model Pricing" settings section (#830). An
 * editable rate card for the per-user model-price overrides shipped in #829:
 * one row per model_prefix with per-Mtok rate inputs, an add-a-rate form, and a
 * delete per row. Save upserts a single row; both save and delete invalidate
 * ['userModelPricing'] and ['spendSummary'] so spend re-prices immediately.
 */
export function ModelPricingEditor() {
  const queryClient = useQueryClient();
  // Per-prefix edit overlay for existing rows (mirrors Billing's edit overlay):
  // the first edit copies the saved row in, so unedited rows render saved values.
  const [edits, setEdits] = useState<Record<string, UserModelPricing>>({});
  const [newRow, setNewRow] = useState<UserModelPricing>(BLANK);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: rates, isLoading, error } = useQuery({
    queryKey: ['userModelPricing'],
    queryFn: () => api.listUserModelPricing(),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['userModelPricing'] });
    queryClient.invalidateQueries({ queryKey: ['spendSummary'] });
  };

  const upsertMutation = useMutation({
    mutationFn: (rate: UserModelPricing) => api.upsertUserModelPricing(rate),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (modelPrefix: string) => api.deleteUserModelPricing(modelPrefix),
    onSuccess: invalidate,
  });

  const rowValue = (saved: UserModelPricing): UserModelPricing =>
    edits[saved.modelPrefix] ?? saved;

  const editRow = (saved: UserModelPricing, field: RateField, value: number) =>
    setEdits((prev) => ({
      ...prev,
      [saved.modelPrefix]: { ...(prev[saved.modelPrefix] ?? saved), [field]: value },
    }));

  const saveRow = (rate: UserModelPricing) => {
    const err = validate(rate);
    if (err) {
      setFormError(err);
      return;
    }
    setFormError(null);
    upsertMutation.mutate(rate);
  };

  const addRow = () => {
    const err = validate(newRow);
    if (err) {
      setFormError(err);
      return;
    }
    setFormError(null);
    upsertMutation.mutate(newRow, { onSuccess: () => setNewRow(BLANK) });
  };

  const rateInputs = (
    prefix: string,
    value: UserModelPricing,
    onChange: (field: RateField, n: number) => void,
  ) =>
    RATE_FIELDS.map((f) => (
      <input
        key={f.key}
        type="number"
        min={0}
        step="any"
        aria-label={`${prefix || 'New'} ${f.label} per Mtok`}
        value={value[f.key]}
        onChange={(e) => onChange(f.key, Number(e.target.value) || 0)}
        className={inputClass(colWidth.inputSm)}
      />
    ));

  return (
    <SectionCard title="Custom Model Pricing" bodyClassName="space-y-4">
      <p className={text.secondary}>
        Set your own per-model rate (USD per million tokens) when you don't pay
        Anthropic list prices — e.g. a $0 plan for a free provider, or Bedrock /
        Vertex per-token rates. Spend prices your usage against these overrides
        and falls back to the standard rate card for any model not listed. Rates
        are matched by model-name prefix (e.g. <code>glm-</code>).
      </p>

      {/* Column headers (hidden from a11y tree; each input carries its own label). */}
      <div className="hidden sm:flex items-center gap-2" aria-hidden="true">
        <span className={cx('text-xs', text.secondary, colWidth.inputLg)}>Model prefix</span>
        {RATE_FIELDS.map((f) => (
          <span key={f.key} className={cx('text-xs', text.secondary, colWidth.inputSm)}>
            {f.label}
          </span>
        ))}
      </div>

      {error ? (
        <Alert variant="error">
          Failed to load custom pricing: {errorMessage(error)}
        </Alert>
      ) : isLoading ? (
        <p className={text.secondary}>Loading custom rates…</p>
      ) : (rates ?? []).length === 0 ? (
        <p className={text.secondary}>
          No custom rates yet. Add one below to price a model at your own rate.
        </p>
      ) : (
        <div className="space-y-2">
          {(rates ?? []).map((saved) => {
            const value = rowValue(saved);
            return (
              <div key={saved.modelPrefix} className="flex flex-wrap items-center gap-2">
                <span className={cx('text-sm font-mono', text.primary, colWidth.inputLg)}>
                  {saved.modelPrefix}
                </span>
                {rateInputs(saved.modelPrefix, value, (field, n) => editRow(saved, field, n))}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => saveRow(value)}
                  disabled={upsertMutation.isPending}
                >
                  Save
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Trash2 />}
                  aria-label={`Delete ${saved.modelPrefix}`}
                  onClick={() => deleteMutation.mutate(saved.modelPrefix)}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add-a-rate form — a distinct labeled form, never a zeroed saved row. */}
      <div className="flex flex-wrap items-end gap-2 border-t pt-4 border-gray-200 dark:border-navy-700">
        <label className="flex flex-col gap-1">
          <span className={cx('text-xs', text.secondary)}>Model prefix</span>
          <input
            type="text"
            placeholder="e.g. glm-5.3"
            aria-label="New model prefix"
            value={newRow.modelPrefix}
            onChange={(e) => setNewRow({ ...newRow, modelPrefix: e.target.value })}
            className={inputClass(colWidth.inputLg)}
          />
        </label>
        {rateInputs('New', newRow, (field, n) => setNewRow({ ...newRow, [field]: n }))}
        <Button
          variant="primary"
          size="sm"
          icon={<Plus />}
          onClick={addRow}
          disabled={upsertMutation.isPending}
        >
          Add rate
        </Button>
      </div>

      {formError && <Alert variant="error">{formError}</Alert>}
      {upsertMutation.isError && (
        <Alert variant="error">Error: {errorMessage(upsertMutation.error)}</Alert>
      )}
      {deleteMutation.isError && (
        <Alert variant="error">Error: {errorMessage(deleteMutation.error)}</Alert>
      )}
      {(upsertMutation.isSuccess || deleteMutation.isSuccess) && !formError && (
        <Alert variant="success">Custom pricing saved.</Alert>
      )}
    </SectionCard>
  );
}
